import React, { useEffect, useRef } from 'react';
import {
  STYLE_EXAMPLE_COPY,
  STYLE_EXAMPLE_MAX_CHARS,
  STYLE_EXAMPLE_MIN_CHARS,
  createStyleExampleItems,
} from '../../utils/saenggibuStyleExamples';

// 내용에 맞춰 높이가 늘어나는 textarea (항목 편집용). 라이브러리 없이 scrollHeight로 맞춘다.
const fitTextareaHeight = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.max(el.scrollHeight, 44)}px`;
};

const AutoGrowTextarea = ({ value, onChange, className, ...rest }) => {
  const ref = useRef(null);

  useEffect(() => {
    fitTextareaHeight(ref.current);
  }, [value]);

  // 폭이 바뀌면(모달 리사이즈·화면 회전) 줄바꿈이 달라지므로 다시 맞춘다.
  // 높이 변경 자체가 observer 를 다시 부르므로 "폭이 바뀐 때만" + 다음 프레임에 맞춘다
  // (ResizeObserver loop 오류 방지)
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let lastWidth = el.clientWidth;
    let frame = 0;
    const refit = () => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        fitTextareaHeight(el);
      });
    };
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(refit);
      observer.observe(el);
      return () => {
        observer.disconnect();
        if (frame) window.cancelAnimationFrame(frame);
      };
    }
    window.addEventListener('resize', refit);
    return () => {
      window.removeEventListener('resize', refit);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <textarea
      ref={ref}
      className={className}
      value={value}
      rows={1}
      onChange={onChange}
      {...rest}
    />
  );
};

const StyleExampleItem = ({ item, index, highlighted, reject, onChange, onRemove }) => {
  const normalized = String(item.text || '').replace(/\s+/g, ' ').trim();
  const length = String(item.text || '').length;
  const over = length > STYLE_EXAMPLE_MAX_CHARS;
  // 서버 MIN_EXAMPLE_CHARS 미만은 저장되지 않으므로 저장 전에 알려준다
  const tooShort = normalized.length > 0 && normalized.length < STYLE_EXAMPLE_MIN_CHARS;
  let countLabel = `${length}/${STYLE_EXAMPLE_MAX_CHARS}`;
  if (over) countLabel = STYLE_EXAMPLE_COPY.overLimitHint;
  else if (tooShort) countLabel = STYLE_EXAMPLE_COPY.tooShortHint;
  return (
    <li
      className={`mystyle-example-item${highlighted ? ' is-rejected' : ''}`}
      data-testid={`mystyle-example-item-${index}`}
    >
      <div className="mystyle-example-row">
        <AutoGrowTextarea
          className="mystyle-example-editor"
          value={item.text || ''}
          onChange={(e) => onChange(item.localKey, e.target.value)}
          aria-label={`${STYLE_EXAMPLE_COPY.itemLabel} ${index + 1}`}
          data-testid={`mystyle-example-editor-${index}`}
        />
        <button
          type="button"
          className="mystyle-example-remove"
          aria-label={`${STYLE_EXAMPLE_COPY.itemLabel} ${index + 1} 삭제`}
          onClick={() => onRemove(item.localKey)}
        >
          ✕
        </button>
      </div>
      <div className="mystyle-example-meta">
        <span
          className={`mystyle-example-count${over || tooShort ? ' is-over' : ''}`}
          data-testid={`mystyle-example-count-${index}`}
        >
          {countLabel}
        </span>
      </div>
      {highlighted && reject?.detail && (
        <p className="mystyle-example-error" data-testid="mystyle-example-error">
          {reject.detail}
        </p>
      )}
    </li>
  );
};

const StyleExamplesSection = ({
  isPro = false,
  limit = 100,
  items = [],
  draft = '',
  open = false,
  reject = null,
  warnings = [],
  onToggle,
  onDraftChange,
  onDraftSubmit,
  onPaste,
  onItemChange,
  onRemove,
}) => {
  const headerLabel = `${STYLE_EXAMPLE_COPY.header} · ${items.length}/${limit}`;
  const hasDraft = Boolean(String(draft || '').trim());

  if (!isPro) {
    return (
      <div className="mystyle-examples" data-testid="mystyle-examples-section">
        <div className="mystyle-examples-title">{STYLE_EXAMPLE_COPY.header}</div>
        <p className="mystyle-examples-why">{STYLE_EXAMPLE_COPY.explainWhy}</p>
        <p className="mystyle-examples-pro">{STYLE_EXAMPLE_COPY.proOnly}</p>
      </div>
    );
  }

  const handleDraftKeyDown = (event) => {
    // Enter = 목록에 넣기, Shift+Enter = 줄바꿈 (붙여넣기 상자에서만 분할한다)
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      if (!hasDraft) return;
      event.preventDefault();
      onDraftSubmit?.();
    }
  };

  return (
    <div className="mystyle-examples" data-testid="mystyle-examples-section">
      <button
        type="button"
        className="mystyle-examples-toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{headerLabel}</span>
        <span className="mystyle-examples-chevron" aria-hidden="true">
          {open ? '⌃' : '⌄'}
        </span>
      </button>
      {open && (
        <div className="mystyle-examples-body">
          <p className="mystyle-examples-why">{STYLE_EXAMPLE_COPY.explainWhy}</p>
          <div className="mystyle-examples-input-row">
            <textarea
              className="mystyle-textarea mystyle-textarea-small mystyle-examples-input"
              rows={3}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={handleDraftKeyDown}
              onPaste={onPaste}
              placeholder={STYLE_EXAMPLE_COPY.placeholder}
              aria-label={STYLE_EXAMPLE_COPY.header}
              data-testid="mystyle-examples-textarea"
            />
            <button
              type="button"
              className="mystyle-examples-add"
              onClick={() => onDraftSubmit?.()}
              disabled={!hasDraft}
            >
              {STYLE_EXAMPLE_COPY.addButton}
            </button>
          </div>
          <div className="mystyle-examples-count" data-testid="mystyle-examples-count">
            <span>{items.length}/{limit}</span>
            <span className="mystyle-examples-add-hint">{STYLE_EXAMPLE_COPY.addHint}</span>
          </div>
          {items.length > 0 && (
            <ul className="mystyle-example-list">
              {items.map((item, index) => (
                <StyleExampleItem
                  key={item.localKey}
                  item={item}
                  index={index}
                  highlighted={reject?.index === index + 1}
                  reject={reject}
                  onChange={onItemChange}
                  onRemove={onRemove}
                />
              ))}
            </ul>
          )}
          {warnings.length > 0 && (
            <ul className="mystyle-example-warnings" data-testid="mystyle-example-warnings">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// 하위 호환: 기존 호출부/테스트가 쓰는 이름
export const ingestStyleExampleDraft = createStyleExampleItems;

export default StyleExamplesSection;
